import React, { useEffect, lazy, Suspense } from 'react';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  Navigate,
} from 'react-router-dom';
import BSThankYou from './components/nexopay-thank-you/bs-thank-you';
import OneClickAutoLimitEnhance from './components/one-click-auto-limit-enhance';
import NachCancellationStartPage from './pages/nach-cancellation/start';
import { CommonErrorBoundary, FullScreenBackdropLoader } from './fibe-ui';

import PopupPermissionPopup from './components/popup-permission-popup';
import { RouteChangeDetector } from './RouteChangeDetector';
import FibeExperienceParentComponent from './components/fibe-experience';
import NachCancellationSubmittedRequestsPage from './pages/nach-cancellation/submitted-request';
import NachCancellationReasonsPage from './pages/nach-cancellation/reason';
import NachCancellationMandateListPage from './pages/nach-cancellation/mandate-list';
import NachCancellationTechnicalIssuePage from './components/nach-cancellation/screens/technical-issue-page';
import RzpCallbackHandler from './components/auto-debit-callback';
import WrapperInactiveCustomerPage from './fibe-ui/wrapper-inactive-customer-page.jsx';

// NOTE: All the normal import MUST be ABOVE lazy loading imports

// Signup
const LoginPage = lazy(() => import('./components/login'));
const PersonalLoan = lazy(() =>
  import('./pages/marketing-login/personal-loan')
);
const InstantPersonalLoan = lazy(() =>
  import('./pages/marketing-login/instant-personal-loan')
);
const LowInterestPersonalLoan = lazy(() =>
  import('./pages/marketing-login/low-interest-personal-loan')
);
const FibeBrand = lazy(() => import('./pages/brand-marketing/fibe-brand'));
const FibeBrandYoutube = lazy(() =>
  import('./pages/brand-marketing/fibe-brand-yt')
);
const WelcomeReferScreen = lazy(() =>
  import('./components/apply-refer-code/welcome-refer-screen')
);
const BankStatementParentContainer = lazy(() =>
  import('./components/bank-statement/bank-statement-conv-ui')
);
const ITRJourneyParent = lazy(() => import('./components/itr-journey'));
const LimitEnhanceConvUiContainer = lazy(() =>
  import('./components/limit-enhance-v2/limit-enhance-v2-conv-ui')
);
const LimitEnhanceConvUiContainerRedirect = lazy(() =>
  import('./components/limit-enhance-v2/limit-enhance-v2-conv-redirect')
);
const TestLEJourney = lazy(() => import('./components/test-le-v2/test-le-v2'));

// Dashboard
const Dashboard = lazy(() => import('./pages/dashboard/index'));
const Checkout = lazy(() => import('./pages/checkout/index'));
const PaymentSuccess = lazy(() =>
  import('./pages/checkout/PaymentProcess/PaymentSuccess')
);
const PaymentFailed = lazy(() =>
  import('./pages/checkout/PaymentProcess/PaymentFailed')
);
const InstantCash = lazy(() => import('./pages/instantCash/index'));
const DynamicTile = lazy(() => import('./pages/dashboard/dynamic-tile'));
const Offers = lazy(() => import('./pages/dashboard/offers'));
const Learn = lazy(() => import('./pages/dashboard/learn'));
const TutorialPlayer = lazy(() => import('./pages/dashboard/tutorial-player'));
const DrpSettlement = lazy(() => import('./pages/dashboard/drp-settlement'));
const PopularProducts = lazy(() =>
  import('./pages/dashboard/popular-products')
);
const CheckoutErrorPage = lazy(() => import('./pages/checkout-error-page'));

// Prepan & Postpan
const PrePanJourney = lazy(() => import('./pages/pre-pan/'));
const PostPanJourney = lazy(() => import('./pages/post-pan'));

// KYC imports
const KycJourney = lazy(() => import('./pages/kyc/kyc-journey.jsx'));
const KYCThankYou = lazy(() =>
  import('./components/nexopay-thank-you/kyc-thank-you')
);
const AutoDebitParent = lazy(() => import('./components/auto-debit'));
const AdThankYou = lazy(() =>
  import('./components/nexopay-thank-you/auto-debit-thank-you')
);
const DigilockerError = lazy(() =>
  import('./pages/kyc/sections/digilockerError')
);
const UnderReview = lazy(() => import('./pages/kyc/sections/UnderReview'));

// Loan Statement and Related
const LoanStatement = lazy(() => import('./pages/dashboard/loan-statement'));
const LoanDetails = lazy(() => import('./pages/dashboard/loan-details'));
const FirstJobber = lazy(() => import('./pages/first-jobber'));

// Fibo related routes
const Fibo = lazy(() => import('./pages/Fibo'));
const ContactUs = lazy(() => import('./pages/Fibo/contact-us.jsx'));
const FAQ = lazy(() => import('./pages/Fibo/faq.jsx'));
const Notification = lazy(() => import('./pages/Fibo/notification'));
const ChatBot = lazy(() => import('./pages/Fibo/chatbot'));

// Lo Hold
const LoHold = lazy(() => import('./pages/lo-hold'));
const UploadPan = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-pan')
);
const UploadBankStatement = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-bank-statement')
);
const UploadCancelledCheque = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-cancelled-cheque')
);
const UploadPassbook = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-passbook')
);
const UploadSelfie = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-selfie')
);
const UploadResidentialAddress = lazy(() =>
  import(
    './components/lo-hold/components/cta-actions/upload-residential-address'
  )
);
const UploadBankDetails = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-bank-details')
);
const UploadAddressDoc = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-address-proof-doc')
);
const UploadKycDoc = lazy(() =>
  import('./components/lo-hold/components/cta-actions/upload-kyc-docs')
);

// Repayment
const Repayment = lazy(() => import('./pages/repayment/index'));
const LoanList = lazy(() => import('./pages/repayment/loanList'));
const LoanDetail = lazy(() => import('./pages/repayment/loanDetails'));
const LoanSchedule = lazy(() => import('./pages/repayment/loanSchedule'));
const RepayOptions = lazy(() => import('./pages/repayment/repayOptions'));

// Suspend & First Jibber
const Suspend = lazy(() => import('./pages/suspend'));
const SuspendLanding = lazy(() => import('./pages/suspend/suspend-landing'));
const OfferLetter = lazy(() => import('./pages/suspend/offer-letter.jsx'));
const SalarySlip = lazy(() => import('./pages/suspend/salary-slip.jsx'));
const MissingSalary = lazy(() => import('./pages/suspend/missing-salary.jsx'));
const FirstJobberLanding = lazy(() =>
  import('./pages/first-jobber/first-jobber-landing')
);
const TechnicalIssue = lazy(() => import('./pages/suspend/technical-issue'));
const TechnicalIssueFJ = lazy(() =>
  import('./pages/first-jobber/technical-issue')
);

// Disbursement
const FibeLanding = lazy(() => import('./pages/fibe-landing'));
const Disbursement = lazy(() => import('./pages/disbursement'));
const RedirectingPage = lazy(() => import('./pages/redirecting-page'));
const ErrorPage = lazy(() => import('./pages/error-page'));
const HdfcConsentLetter = lazy(() =>
  import('./pages/checkout/HdfcConsentLetter')
);

// Aboout US & TnC & Misc
const AboutUs = lazy(() => import('./pages/about-us/about-us.jsx'));
const FairPractices = lazy(() => import('./pages/about-us/fair-practices'));
const LendingPartners = lazy(() => import('./pages/about-us/lending-partners'));
const PrivacyPolicy = lazy(() => import('./pages/about-us/privacy-policy'));
const TermsandConditions = lazy(() =>
  import('./pages/about-us/terms-and-conditions')
);

// Edit Profile
const EditProfilePage = lazy(() => import('./pages/edit-profile'));
const UploadBS = lazy(() =>
  import('./components/edit-profile/components/bankStatementAckScreen')
);

// Cash Loan
const CashLoanPage = lazy(() => import('./pages/cash-loan'));
const LEQuestionsPage = lazy(() => import('./pages/le-questions/index'));

// Inactive usER
const InactiveCustomerPage = lazy(() => './fibe-ui/inactive-customer-page');

// Misc
const PathNotFound = lazy(() => import('./components/not-found/404'));
const CommonError = lazy(() => import('./pages/CommonError'));
const WorkInProgress = lazy(() => import('./pages/wip'));

// Test Routes
const TestComp = lazy(() => import('./components/common/test-comp'));
const HyperVergeCameraTest = lazy(() =>
  import('./pages/hyperverge-camera-test')
);
const CameraPermissionPopup = lazy(() =>
  import('./components/camera-permission-popup')
);
const LocationPermissionPopup = lazy(() =>
  import('./components/location-permission-popup')
);

// disbursement-journey
const DisbursementJourney = lazy(() => import('./pages/disbursement-journey'));
const ChooseTenure = lazy(() =>
  import('./components/disbursement-journey/choose-tenure')
);
const LoanConfimration = lazy(() =>
  import('./components/disbursement-journey/loan-confirmation.jsx')
);
const DisbursementSuccess = lazy(() =>
  import('./components/disbursement-journey/transition-success.jsx')
);
const DisbursementPending = lazy(() =>
  import('./components/disbursement-journey/transition-pending.jsx')
);
const DisbursementFailed = lazy(() =>
  import('./components/disbursement-journey/transition-faild.jsx')
);
const InstantCashPage = lazy(() =>
  import('./components/disbursement-journey/instant-cash-page.jsx')
);
const AvailCash = lazy(() =>
  import('./components/disbursement-journey/avail-cash.jsx')
);

const routesToBeMadeLive = [
  'limit-enhance-v2',
  'limit-enhance-v2-redirect',
  'dynamic-tile',
  'fair-practices',
  'lending-partners',
  'privacy-policy',
  'terms-and-conditions',
];

const UseRouteWrapper = ({ children }) => {
  const pathname = window.location.pathname;
  // useEffect(() => {
  //   let scriptHtmlElOne;
  //   let scriptHtmlElTwo;
  //   let scriptHtmlElThree;
  //   if (pathname !== '/SignUp' && pathname !== '/') {
  //     scriptHtmlElTwo = document.createElement('script');

  //     scriptHtmlElTwo.src =
  //       'https://maps.googleapis.com/maps/api/js?key=AIzaSyCl0K5nUBg0JLLEX_w3Lrj0ii16TWHIHC8&libraries=places';
  //     // scriptHtmlElTwo.async = true; // DOCS: DON'T add async and defer simultaneously
  //     // DOCS: As defer prevernts the blockage of html parsing either during download or execution of JS
  //     // Async => Blocks the html parsing while execution of JS
  //     scriptHtmlElTwo.defer = true;

  //     // DOCS: append the link to the body of the document
  //     document.body.appendChild(scriptHtmlElTwo);

  //     return () => {
  //       // DOCS: Remove the link post unmounting of the screen
  //       document.body.removeChild(scriptHtmlElTwo);
  //     };
  //   }
  // }, [pathname]);

  return <>{children}</>;
};

const LocalRouter = () => {
  return (
    <Router>
      <CommonErrorBoundary>
        <RouteChangeDetector />
        <Suspense fallback={<FullScreenBackdropLoader isLoading={true} />}>
          <Routes>
            {/* SignUp Routes */}
            <Route
              path="/"
              element={
                <UseRouteWrapper>
                  <Navigate to="/SignUp" replace />
                </UseRouteWrapper>
              }
            />

            <Route
              path="/portal"
              element={
                <UseRouteWrapper>
                  <Navigate to="/SignUp" replace />
                </UseRouteWrapper>
              }
            />

            {/* SignUp Routes */}
            <Route
              path="SignUp"
              element={
                <UseRouteWrapper>
                  <LoginPage />
                </UseRouteWrapper>
              }
            />

            <Route
              path="fibe-brand"
              element={
                <UseRouteWrapper>
                  <FibeBrand />
                </UseRouteWrapper>
              }
            />

            <Route
              path="fibe-brand-yt"
              element={
                <UseRouteWrapper>
                  <FibeBrandYoutube />
                </UseRouteWrapper>
              }
            />

            <Route
              path="personal-loan"
              element={
                <UseRouteWrapper>
                  <PersonalLoan />
                </UseRouteWrapper>
              }
            />

            <Route
              path="instant-personal-loan"
              element={
                <UseRouteWrapper>
                  <InstantPersonalLoan />
                </UseRouteWrapper>
              }
            />

            {/* Dashboard Related Routes */}
            <Route
              path="dashboard"
              element={
                <UseRouteWrapper>
                  <Dashboard />
                </UseRouteWrapper>
              }
            />
            <Route
              path="checkout"
              element={
                <UseRouteWrapper>
                  <Checkout />
                </UseRouteWrapper>
              }
            />
            <Route
              path="consent-letter"
              element={
                <UseRouteWrapper>
                  <HdfcConsentLetter />
                </UseRouteWrapper>
              }
            />
            <Route
              path="payment-success"
              element={
                <UseRouteWrapper>
                  <PaymentSuccess />
                </UseRouteWrapper>
              }
            />
            <Route
              path="payment-failed"
              element={
                <UseRouteWrapper>
                  <PaymentFailed />
                </UseRouteWrapper>
              }
            />
            <Route
              path="instant-cash-loan"
              element={
                <UseRouteWrapper>
                  <InstantCash />
                </UseRouteWrapper>
              }
            />
            <Route
              path="dynamic-tile"
              element={
                <UseRouteWrapper>
                  <DynamicTile />
                </UseRouteWrapper>
              }
            />
            <Route
              path="offers"
              element={
                <UseRouteWrapper>
                  <Offers />
                </UseRouteWrapper>
              }
            />
            <Route
              path="learn"
              element={
                <UseRouteWrapper>
                  <Learn />
                </UseRouteWrapper>
              }
            />
            <Route
              path="tutorial-player"
              element={
                <UseRouteWrapper>
                  <TutorialPlayer />
                </UseRouteWrapper>
              }
            />
            <Route
              path="drp-settlement"
              element={
                <UseRouteWrapper>
                  <DrpSettlement />
                </UseRouteWrapper>
              }
            />
            <Route
              path="popular-products"
              element={
                <UseRouteWrapper>
                  <PopularProducts />
                </UseRouteWrapper>
              }
            />
            <Route
              path="checkout-error-page"
              element={
                <UseRouteWrapper>
                  <CheckoutErrorPage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="low-interest-personal-loan"
              element={
                <UseRouteWrapper>
                  <LowInterestPersonalLoan />
                </UseRouteWrapper>
              }
            />

            <Route
              path="apply-refer-code"
              element={
                <UseRouteWrapper>
                  <WelcomeReferScreen />
                </UseRouteWrapper>
              }
            />

            {/* Bank Statement/ITR/Limit Enhancement Routes */}
            <Route
              path="bank-statement"
              element={
                <UseRouteWrapper>
                  <BankStatementParentContainer />
                </UseRouteWrapper>
              }
            />
            <Route
              path="le-bank-statement"
              element={
                <UseRouteWrapper>
                  <BankStatementParentContainer />
                </UseRouteWrapper>
              }
            />

            <Route
              path="itr-journey"
              element={
                <UseRouteWrapper>
                  <ITRJourneyParent />
                </UseRouteWrapper>
              }
            />

            <Route
              path="limit-enhance-v2"
              element={
                <UseRouteWrapper>
                  <LimitEnhanceConvUiContainer />
                </UseRouteWrapper>
              }
            />
            <Route
              path="limit-enhance-v2-redirect"
              element={
                <UseRouteWrapper>
                  <LimitEnhanceConvUiContainerRedirect />
                </UseRouteWrapper>
              }
            />
            <Route
              path="test-le-v2"
              element={
                <UseRouteWrapper>
                  <TestLEJourney />
                </UseRouteWrapper>
              }
            />

            {/* Dashboard Related Routes */}
            <Route
              path="dashboard"
              element={
                <UseRouteWrapper>
                  <Dashboard />
                </UseRouteWrapper>
              }
            />
            <Route
              path="checkout"
              element={
                <UseRouteWrapper>
                  <Checkout />
                </UseRouteWrapper>
              }
            />
            <Route
              path="payment-success"
              element={
                <UseRouteWrapper>
                  <PaymentSuccess />
                </UseRouteWrapper>
              }
            />
            <Route
              path="payment-failed"
              element={
                <UseRouteWrapper>
                  <PaymentFailed />
                </UseRouteWrapper>
              }
            />
            <Route
              path="instant-cash-loan"
              element={
                <UseRouteWrapper>
                  <InstantCash />
                </UseRouteWrapper>
              }
            />
            <Route
              path="dynamic-tile"
              element={
                <UseRouteWrapper>
                  <DynamicTile />
                </UseRouteWrapper>
              }
            />
            <Route
              path="offers"
              element={
                <UseRouteWrapper>
                  <Offers />
                </UseRouteWrapper>
              }
            />
            <Route
              path="learn"
              element={
                <UseRouteWrapper>
                  <Learn />
                </UseRouteWrapper>
              }
            />
            <Route
              path="tutorial-player"
              element={
                <UseRouteWrapper>
                  <TutorialPlayer />
                </UseRouteWrapper>
              }
            />
            <Route
              path="drp-settlement"
              element={
                <UseRouteWrapper>
                  <DrpSettlement />
                </UseRouteWrapper>
              }
            />
            <Route
              path="popular-products"
              element={
                <UseRouteWrapper>
                  <PopularProducts />
                </UseRouteWrapper>
              }
            />
            <Route
              path="checkout-error-page"
              element={
                <UseRouteWrapper>
                  <CheckoutErrorPage />
                </UseRouteWrapper>
              }
            />
            {/* KYC Related Routes */}
            <Route
              path="kyc-process"
              element={
                <UseRouteWrapper>
                  <KycJourney />
                </UseRouteWrapper>
              }
            />
            <Route
              path="kyc-thank-you"
              element={
                <UseRouteWrapper>
                  <KYCThankYou />
                </UseRouteWrapper>
              }
            />
            <Route
              path="bs-thank-you"
              element={
                <UseRouteWrapper>
                  <BSThankYou />
                </UseRouteWrapper>
              }
            />
            <Route
              path="auto-debit"
              element={
                <UseRouteWrapper>
                  <AutoDebitParent />
                </UseRouteWrapper>
              }
            />
            <Route
              path="nach-cancellation/start"
              element={
                <UseRouteWrapper>
                  <NachCancellationStartPage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="nach-cancellation/mandate-list"
              element={
                <UseRouteWrapper>
                  <NachCancellationMandateListPage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="nach-cancellation/reason"
              element={
                <UseRouteWrapper>
                  <NachCancellationReasonsPage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="nach-cancellation/technical-issue"
              element={
                <UseRouteWrapper>
                  <NachCancellationTechnicalIssuePage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="nach-cancellation/submitted-request"
              element={
                <UseRouteWrapper>
                  <NachCancellationSubmittedRequestsPage />
                </UseRouteWrapper>
              }
            />

            <Route
              path="ad-thank-you"
              element={
                <UseRouteWrapper>
                  <AdThankYou />
                </UseRouteWrapper>
              }
            />

            <Route
              path="digilocker-error"
              element={
                <UseRouteWrapper>
                  <DigilockerError />
                </UseRouteWrapper>
              }
            />
            <Route
              path="under-review"
              element={
                <UseRouteWrapper>
                  <UnderReview />
                </UseRouteWrapper>
              }
            />
            {/* Prepan */}
            <Route
              path="pre-pan"
              element={
                <UseRouteWrapper>
                  <PrePanJourney />
                </UseRouteWrapper>
              }
            />
            {/* Postpan */}
            <Route
              path="post-pan"
              element={
                <UseRouteWrapper>
                  <PostPanJourney />
                </UseRouteWrapper>
              }
            />
            {/* Loan Statement and Related */}
            <Route
              path="loan-statement"
              element={
                <UseRouteWrapper>
                  <LoanStatement />
                </UseRouteWrapper>
              }
            />
            <Route
              path="loan-details"
              element={
                <UseRouteWrapper>
                  <LoanDetails />
                </UseRouteWrapper>
              }
            />
            <Route
              path="first-jobber"
              element={
                <UseRouteWrapper>
                  <FirstJobber />
                </UseRouteWrapper>
              }
            />
            {/* Fibo and Related */}
            <Route
              path="Fibo"
              element={
                <UseRouteWrapper>
                  <Fibo />
                </UseRouteWrapper>
              }
            />
            <Route
              path="contact-us"
              element={
                <UseRouteWrapper>
                  <ContactUs />
                </UseRouteWrapper>
              }
            />
            {/* <Route
              path="faq"
              element={
                <UseRouteWrapper>
                  <FAQ />
                </UseRouteWrapper>
              }
            /> */}
            <Route
              path="notification"
              element={
                <UseRouteWrapper>
                  <Notification />
                </UseRouteWrapper>
              }
            />
            <Route
              path="chatbot"
              element={
                <UseRouteWrapper>
                  <ChatBot />
                </UseRouteWrapper>
              }
            />

            {/* Lo Hold */}
            <Route
              path="lo-hold"
              element={
                <UseRouteWrapper>
                  <LoHold />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-pan"
              element={
                <UseRouteWrapper>
                  <UploadPan />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-bank-statement"
              element={
                <UseRouteWrapper>
                  <UploadBankStatement />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-cancelled-cheque"
              element={
                <UseRouteWrapper>
                  <UploadCancelledCheque />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-passbook"
              element={
                <UseRouteWrapper>
                  <UploadPassbook />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-selfie"
              element={
                <UseRouteWrapper>
                  <UploadSelfie />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-address"
              element={
                <UseRouteWrapper>
                  <UploadResidentialAddress />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-bank-details"
              element={
                <UseRouteWrapper>
                  <UploadBankDetails />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-address-doc"
              element={
                <UseRouteWrapper>
                  <UploadAddressDoc />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upload-kyc-doc"
              element={
                <UseRouteWrapper>
                  <UploadKycDoc />
                </UseRouteWrapper>
              }
            />

            {/* Repayment Routes */}
            <Route
              path="repayment"
              element={
                <UseRouteWrapper>
                  <Repayment />
                </UseRouteWrapper>
              }
            />
            <Route
              path="loan-list"
              element={
                <UseRouteWrapper>
                  <LoanList />
                </UseRouteWrapper>
              }
            />
            <Route
              path="repayment-loan-details"
              element={
                <UseRouteWrapper>
                  <LoanDetail />
                </UseRouteWrapper>
              }
            />
            <Route
              path="loan-schedule"
              element={
                <UseRouteWrapper>
                  <LoanSchedule />
                </UseRouteWrapper>
              }
            />
            <Route
              path="loan-payment"
              element={
                <UseRouteWrapper>
                  <RepayOptions />
                </UseRouteWrapper>
              }
            />

            {/* Suspend Routes */}
            <Route
              path="suspend-landing"
              element={
                <UseRouteWrapper>
                  <SuspendLanding />
                </UseRouteWrapper>
              }
            />
            <Route
              path="first-jobber-landing"
              element={
                <UseRouteWrapper>
                  <FirstJobberLanding />
                </UseRouteWrapper>
              }
            />
            <Route
              path="suspend"
              element={
                <UseRouteWrapper>
                  <Suspend />
                </UseRouteWrapper>
              }
            />
            <Route
              path="offer-letter"
              element={
                <UseRouteWrapper>
                  <OfferLetter />
                </UseRouteWrapper>
              }
            />
            <Route
              path="salary-slip"
              element={
                <UseRouteWrapper>
                  <SalarySlip />
                </UseRouteWrapper>
              }
            />
            <Route
              path="missing-salary"
              element={
                <UseRouteWrapper>
                  <MissingSalary />
                </UseRouteWrapper>
              }
            />
            <Route
              path="technical-issue"
              element={
                <UseRouteWrapper>
                  <TechnicalIssue />
                </UseRouteWrapper>
              }
            />
            <Route
              path="technical-issue-fj"
              element={
                <UseRouteWrapper>
                  <TechnicalIssueFJ />
                </UseRouteWrapper>
              }
            />

            {/* Disbursement Landing page and Related */}
            <Route
              path="fibe-landing"
              element={
                <UseRouteWrapper>
                  <FibeLanding />
                </UseRouteWrapper>
              }
            />
            {/* Old branding include es-landing as a baseUrl like Airtel Payments Bank */}
            <Route
              path="es-landing"
              element={
                <UseRouteWrapper>
                  <FibeLanding />
                </UseRouteWrapper>
              }
            />
            <Route
              path="disbursement"
              element={
                <UseRouteWrapper>
                  <Disbursement />
                </UseRouteWrapper>
              }
            />
            <Route
              path="redirecting-page"
              element={
                <UseRouteWrapper>
                  <RedirectingPage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="error-page"
              element={
                <UseRouteWrapper>
                  <ErrorPage />
                </UseRouteWrapper>
              }
            />

            {/* Test Routes */}
            <Route
              path="_/test"
              element={
                <UseRouteWrapper>
                  <TestComp />
                </UseRouteWrapper>
              }
            />
            <Route
              path="hv-test"
              element={
                <UseRouteWrapper>
                  <HyperVergeCameraTest />
                </UseRouteWrapper>
              }
            />
            <Route
              path="test/camera-permission"
              element={
                <UseRouteWrapper>
                  <CameraPermissionPopup />
                </UseRouteWrapper>
              }
            />
            <Route
              path="test/location-permission"
              element={
                <UseRouteWrapper>
                  <LocationPermissionPopup />
                </UseRouteWrapper>
              }
            />
            <Route
              path="test/popup-permission"
              element={
                <UseRouteWrapper>
                  <PopupPermissionPopup />
                </UseRouteWrapper>
              }
            />

            <Route
              path="popup-permission"
              element={
                <UseRouteWrapper>
                  <PopupPermissionPopup />
                </UseRouteWrapper>
              }
            />
            {/* About Us Routes */}
            <Route
              path="about-us"
              element={
                <UseRouteWrapper>
                  <AboutUs />
                </UseRouteWrapper>
              }
            />
            <Route
              path="fair-practices"
              element={
                <UseRouteWrapper>
                  <FairPractices />
                </UseRouteWrapper>
              }
            />
            <Route
              path="lending-partners"
              element={
                <UseRouteWrapper>
                  <LendingPartners />
                </UseRouteWrapper>
              }
            />
            <Route
              path="privacy-policy"
              element={
                <UseRouteWrapper>
                  <PrivacyPolicy />
                </UseRouteWrapper>
              }
            />
            <Route
              path="terms-and-conditions"
              element={
                <UseRouteWrapper>
                  <TermsandConditions />
                </UseRouteWrapper>
              }
            />

            {/* Edit User Profile */}
            <Route
              path="edit-profile"
              element={
                <UseRouteWrapper>
                  <EditProfilePage />
                </UseRouteWrapper>
              }
            />
            {/* Upload Bank statement ack screen post successful bs edit */}
            <Route
              path="upload-bs"
              element={
                <UseRouteWrapper>
                  <UploadBS />
                </UseRouteWrapper>
              }
            />

            {/* Cash Loan Routes */}
            <Route
              path="cash-loan"
              element={
                <UseRouteWrapper>
                  <CashLoanPage />
                </UseRouteWrapper>
              }
            />

            <Route
              path="redo-journey-screen"
              element={
                <UseRouteWrapper>
                  <CashLoanPage isRedoJourney={true} />
                </UseRouteWrapper>
              }
            />
            <Route
              path="upgrade-your-limit"
              element={
                <UseRouteWrapper>
                  <LEQuestionsPage />
                </UseRouteWrapper>
              }
            />

            <Route
              path="auto-limit-enhance"
              element={
                <UseRouteWrapper>
                  <OneClickAutoLimitEnhance />
                </UseRouteWrapper>
              }
            />

            {/* Inactive Customer Route */}
            <Route
              path="inactive-customer"
              element={
                <UseRouteWrapper>
                  <InactiveCustomerPage />
                </UseRouteWrapper>
              }
            />
            {/* Under maintainance / Disaster Recovery Route */}
            <Route
              path="under-maintainance"
              element={<InactiveCustomerPage />}
            />
            {/* Duplicate Pan Card*/}
            <Route
              path="duplicate-pan"
              element={<WrapperInactiveCustomerPage />}
            />
            {/* Test Routes */}
            <Route
              path="_/test"
              element={
                <UseRouteWrapper>
                  <TestComp />
                </UseRouteWrapper>
              }
            />
            <Route
              path="hv-test"
              element={
                <UseRouteWrapper>
                  <HyperVergeCameraTest />
                </UseRouteWrapper>
              }
            />
            <Route
              path="test/camera-permission"
              element={
                <UseRouteWrapper>
                  <CameraPermissionPopup />
                </UseRouteWrapper>
              }
            />
            <Route
              path="test/location-permission"
              element={
                <UseRouteWrapper>
                  <LocationPermissionPopup />
                </UseRouteWrapper>
              }
            />

            {/* Default 404 type of route */}
            <Route
              path="*"
              element={
                <UseRouteWrapper>
                  <PathNotFound />
                </UseRouteWrapper>
              }
            />
            <Route
              path="not-found"
              element={
                <UseRouteWrapper>
                  <PathNotFound />
                </UseRouteWrapper>
              }
            />

            {/* Commmon error page, on cta click user will be navigated to dashboard */}
            <Route
              path="error"
              element={
                <UseRouteWrapper>
                  <CommonError />
                </UseRouteWrapper>
              }
            />

            <Route
              path="wip"
              element={
                <UseRouteWrapper>
                  <WorkInProgress />
                </UseRouteWrapper>
              }
            />

            {/* Google Spot Related Routes */}
            <Route
              path="fibe-experience"
              element={
                <UseRouteWrapper>
                  <FibeExperienceParentComponent />
                </UseRouteWrapper>
              }
            />

            <Route
              path="auto-debit-callback"
              element={
                <UseRouteWrapper>
                  <RzpCallbackHandler />
                </UseRouteWrapper>
              }
            />

            <Route
              path="es-experience"
              element={
                <UseRouteWrapper>
                  <FibeExperienceParentComponent />
                </UseRouteWrapper>
              }
            />
            <Route
              path="disbursement-journey"
              element={
                <UseRouteWrapper>
                  <DisbursementJourney />
                </UseRouteWrapper>
              }
            />
            <Route
              path="choose-tenure"
              element={
                <UseRouteWrapper>
                  <ChooseTenure />
                </UseRouteWrapper>
              }
            />
            <Route
              path="loan-confirmation"
              element={
                <UseRouteWrapper>
                  <LoanConfimration />
                </UseRouteWrapper>
              }
            />
            <Route
              path="disbursement-success"
              element={
                <UseRouteWrapper>
                  <DisbursementSuccess />
                </UseRouteWrapper>
              }
            />
            <Route
              path="disbursement-pending"
              element={
                <UseRouteWrapper>
                  <DisbursementPending />
                </UseRouteWrapper>
              }
            />
            <Route
              path="disbursement-failed"
              element={
                <UseRouteWrapper>
                  <DisbursementFailed />
                </UseRouteWrapper>
              }
            />
            <Route
              path="instant-cash-page"
              element={
                <UseRouteWrapper>
                  <InstantCashPage />
                </UseRouteWrapper>
              }
            />
            <Route
              path="avail-cash-page"
              element={
                <UseRouteWrapper>
                  <AvailCash />
                </UseRouteWrapper>
              }
            />
          </Routes>
        </Suspense>
      </CommonErrorBoundary>
    </Router>
  );
};

export default LocalRouter;
